import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import WinnersAssetUpload from '../components/WinnersAssetUpload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LogoutIcon from '@mui/icons-material/Logout';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

export default function WinnersAssetPage() {
    const [context, setContext] = useState(null);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        navigate('/login');
    };

    const loadContext = useCallback(async () => {
        try {
            setError('');
            const response = await api.get('/api/winners-assets/context');
            setContext(response.data);
        } catch (requestError) {
            setError(requestError?.response?.data?.message || 'Unable to load this area. Please try again.');
        }
    }, []);

    useEffect(() => { loadContext(); }, [loadContext]);

    if (!context && !error) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    if (error) {
        return (
            <Box sx={{ maxWidth: 720, mx: 'auto', py: 6 }}>
                <Alert severity="error">{error}</Alert>
                <Button sx={{ mt: 2 }} startIcon={<LogoutIcon />} onClick={logout}>Logout</Button>
            </Box>
        );
    }

    if (!context.eligible) {
        return (
            <Box sx={{ maxWidth: 720, mx: 'auto', py: { xs: 4, md: 8 } }}>
                <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
                    <BrandBar />
                    <Box sx={{ p: { xs: 3, md: 5 } }}>
                        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 2 }}>Winners Asset Portal</Typography>
                        <Typography>
                            This area is available to designated {context.programYear} Retool Your School winning schools. If you believe your school should have access, please contact the Retool Your School team at{' '}
                            <a href="mailto:retoolyourschool@homedepot.com">retoolyourschool@homedepot.com</a>.
                        </Typography>
                        <Button variant="contained" sx={{ mt: 3 }} onClick={logout}>Back To Login</Button>
                    </Box>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 960, mx: 'auto', py: { xs: 2, md: 4 } }}>
            <BrandBar onLogout={logout} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, py: 4, flexWrap: 'wrap' }}>
                {context.schoolLogo && (
                    <Box component="img" src={context.schoolLogo} alt={`${context.schoolName} logo`} sx={{ width: 112, height: 112, objectFit: 'contain' }} />
                )}
                <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>{context.schoolName}</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        Upload your project documents and before-project photos below. Use <strong>Project Details</strong> for project documents, and <strong>Before Photos &amp; Supporting Files</strong> for photos or materials showing the space before work begins.
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        Files are saved as soon as they upload, so you can return anytime to review, add, replace, or remove files. The file limits are shown in each section.
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'grid', gap: 3 }}>
                <WinnersAssetUpload
                    title="Project Details"
                    category="project_details"
                    files={context.projectDetails}
                    maxFiles={context.projectDetailsMaxFiles}
                    maxFileSize={context.maxFileSize}
                    onChanged={loadContext}
                />
                <WinnersAssetUpload
                    title="Before Photos & Supporting Files"
                    category="before_photos"
                    files={context.beforePhotos}
                    maxFiles={context.beforePhotosMaxFiles}
                    maxFileSize={context.maxFileSize}
                    onChanged={loadContext}
                />
            </Box>
        </Box>
    );
}

function BrandBar({ onLogout }) {
    return (
        <Box sx={{ bgcolor: '#111', px: { xs: 2, md: 3 }, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box component="img" src="/ryslogo.png" alt="Retool Your School" sx={{ height: { xs: 42, md: 54 }, maxWidth: '65%', objectFit: 'contain' }} />
            {onLogout && <Button color="inherit" startIcon={<LogoutIcon />} onClick={onLogout} sx={{ color: '#fff' }}>Logout</Button>}
        </Box>
    );
}